import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { ApiError, apiDescarrega } from '@/api/client';
import { useMissatgeError } from '@/api/errors';
import {
  useAuditoria,
  useDocument,
  useEtag,
  useRetiraDocument,
  useVersionsDocument,
  type AmbitDocument,
  type DocumentAdminDto,
  type DocumentVersioDto,
} from '@/api/administracio';
import { useDesaDocument, useRestauraDocument } from '@/api/ajustosSistema';
import { nomUsuari, useUsuaris } from '@/api/planificacio';
import { Desplegable, type OpcioDesplegable } from '@/components/Desplegable';
import { Icona } from '@/components/Icona';
import { useFormat } from '@/i18n/useFormat';
import { Toolbar } from '@/layout/Toolbar';
import { Carregant, ErrorCarrega } from '@/pages/EstatsPagina';
import { Buit, Camp } from '@/pages/operacio/comu';
import { useMe } from '@/permisos/MeContext';
import { textAccioAuditoria } from './auditoria';
import { BarraCanvis } from './marc/canvis';
import { Confirmacio } from './marc/Calaix';
import { useEstructura } from './marc/estructura';
import { CapPanell, ErrorDesa } from './comu';
import { badgeEstatDocument, midaLlegible } from './utils';
import { W34PujaDocument } from './W34PujaDocument';
import './administracio.css';
import './sistema.css';

const AMBITS: AmbitDocument[] = ['SERVEI', 'AREA', 'DEPARTAMENT'];
type Pestanya = 'dades' | 'versions' | 'acces' | 'activitat';

const badgeEscaneig = (e: DocumentVersioDto['estatEscaneig']) =>
  e === 'NET' ? 'badge--ok' : e === 'PENDENT' ? 'badge--avis' : 'badge--critic';

interface Dades {
  titol: string;
  categoria: string;
  ambit: AmbitDocument;
  responsableId: number | null;
}

const dadesDe = (d: DocumentAdminDto): Dades => ({
  titol: d.titol,
  categoria: d.categoria ?? '',
  ambit: d.ambit,
  responsableId: d.responsableId ?? null,
});

/** Nom del destinatari de l'àmbit: el servei, la seva àrea o el seu departament. */
function useNomAmbit(serveiId: number) {
  const e = useEstructura();
  const s = e.servei(serveiId);
  const a = e.area(s?.areaId);
  const dep = e.departament(a?.departamentId);
  return {
    ruta: e.ruta('SERVEI', serveiId),
    nom: (amb: AmbitDocument) =>
      (amb === 'SERVEI' ? s?.nom : amb === 'AREA' ? a?.nom : dep?.nom) ?? '—',
  };
}

function PestanyaDades({ d, editable }: { d: DocumentAdminDto; editable: boolean }) {
  const { t } = useTranslation('administracio');
  const f = useFormat();
  const usuaris = useUsuaris();
  const { etag } = useEtag('/documents', d.id);
  const desa = useDesaDocument(etag);
  const ambit = useNomAmbit(d.serveiId);
  const inicial = dadesDe(d);
  const [dades, setDades] = useState<Dades>(inicial);
  const vigent = d.versioVigent;
  const n =
    Number(dades.titol !== inicial.titol) +
    Number(dades.categoria !== inicial.categoria) +
    Number(dades.ambit !== inicial.ambit) +
    Number(dades.responsableId !== inicial.responsableId);
  const valid = dades.titol.trim() !== '';
  const opcionsResponsable: OpcioDesplegable<number | ''>[] = [
    { valor: '', etiqueta: t('sistema.documents.senseResponsable') },
    ...(usuaris.data ?? []).map((u) => ({
      valor: u.id,
      etiqueta: nomUsuari(u),
      detall: u.email ?? undefined,
    })),
  ];
  // Si el responsable actual no és al directori, es manté visible pel nom que dona el servidor.
  if (d.responsableId != null && !opcionsResponsable.some((o) => o.valor === d.responsableId)) {
    opcionsResponsable.push({
      valor: d.responsableId,
      etiqueta: d.responsable ?? `#${d.responsableId}`,
      detall: undefined,
    });
  }

  return (
    <div className="admin-mes-lateral">
      <div className="panel">
        <CapPanell titol={t('documents.dades')} icona="pencil" />
        <div className="panel__body form">
          {!editable ? (
            <p className="sis-nota-gris" style={{ margin: 0 }}>
              <Icona nom="lock" />{' '}
              {d.estat === 'RETIRAT'
                ? t('sistema.documents.retiratNoEditable')
                : t('sistema.documents.senseEdicio')}
            </p>
          ) : null}
          <Camp etiqueta={t('documents.titol')}>
            <input
              className="input"
              value={dades.titol}
              disabled={!editable}
              aria-invalid={!valid}
              onChange={(e) => setDades({ ...dades, titol: e.target.value })}
            />
          </Camp>
          <div className="grid grid-2" style={{ gap: 12 }}>
            <Camp etiqueta={t('documents.categoria')}>
              <input
                className="input"
                value={dades.categoria}
                disabled={!editable}
                placeholder={t('sistema.documents.categoriaPlaceholder')}
                onChange={(e) => setDades({ ...dades, categoria: e.target.value })}
              />
            </Camp>
            <Camp etiqueta={t('documents.responsable')}>
              <Desplegable
                aria-label={t('documents.responsable')}
                valor={dades.responsableId ?? ''}
                disabled={!editable}
                cerca
                opcions={opcionsResponsable}
                onCanvia={(v) => setDades({ ...dades, responsableId: v === '' ? null : v })}
              />
            </Camp>
          </div>
          <div className="camp">
            <span className="camp__label">{t('sistema.documents.quiConsulta')}</span>
            <div className="sis-opcions-ambit" role="radiogroup" aria-label={t('documents.ambit')}>
              {AMBITS.map((a) => (
                <label key={a} className="sis-opcio-ambit">
                  <input
                    type="radio"
                    name={`ambit-${d.id}`}
                    checked={dades.ambit === a}
                    disabled={!editable}
                    onChange={() => setDades({ ...dades, ambit: a })}
                  />
                  <span>
                    <strong>{t(`sistema.documents.ambit.${a}`)}</strong>
                    <span className="dim">{ambit.nom(a)}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>
          <ErrorDesa error={desa.error} />
          {desa.isSuccess && n === 0 ? (
            <p className="ok-text" role="status">
              {t('comu.desat')}
            </p>
          ) : null}
        </div>
      </div>
      <div className="panel">
        <CapPanell titol={t('documents.metadades')} />
        <dl className="panel__body sis-dl">
          <div>
            <dt>{t('documents.codi')}</dt>
            <dd className="mono">{d.codi ?? `#${d.id}`}</dd>
          </div>
          <div>
            <dt>{t('documents.servei')}</dt>
            <dd>
              {d.servei ?? '—'}
              {ambit.ruta ? <div className="t-caption dim">{ambit.ruta}</div> : null}
            </dd>
          </div>
          <div>
            <dt>{t('documents.versioVigent')}</dt>
            <dd className="mono">
              {vigent ? t('documents.vN', { n: vigent.versio }) : t('documents.senseVigent')}
            </dd>
          </div>
          <div>
            <dt>{t('documents.actualitzat')}</dt>
            <dd>{d.updatedAt ? f.dataHora(d.updatedAt) : '—'}</dd>
          </div>
          <div>
            <dt>{t('documents.analisi')}</dt>
            <dd>
              {vigent ? (
                <span className={`badge ${badgeEscaneig(vigent.estatEscaneig)}`}>
                  {t(`documents.escaneig.${vigent.estatEscaneig}`)}
                </span>
              ) : (
                '—'
              )}
            </dd>
          </div>
        </dl>
      </div>
      {editable ? (
        <BarraCanvis
          n={n}
          nom={d.titol}
          desant={desa.isPending}
          desactivat={!valid}
          onDescarta={() => {
            setDades(inicial);
            desa.reset();
          }}
          onDesa={() =>
            desa.mutate({
              id: d.id,
              titol: dades.titol.trim(),
              categoria: dades.categoria.trim() || null,
              ambit: dades.ambit,
              responsableId: dades.responsableId,
            })
          }
        />
      ) : null}
    </div>
  );
}

function PestanyaVersions({
  d,
  descarrega,
}: {
  d: DocumentAdminDto;
  descarrega: (v: DocumentVersioDto) => void;
}) {
  const { t } = useTranslation('administracio');
  const f = useFormat();
  const versions = useVersionsDocument(d.id);
  const vigent = d.versioVigent;
  const llista = versions.data ?? [];
  return (
    <div className="panel">
      <CapPanell titol={t('documents.versions')} extra={t('documents.conservacio')} />
      {versions.isError ? (
        <ErrorCarrega onReintenta={() => void versions.refetch()} />
      ) : llista.length === 0 ? (
        <Buit icona="archive">
          {versions.isLoading ? t('comu.carregant') : t('documents.senseVersions')}
        </Buit>
      ) : (
        <div className="sis-taula-scroll">
          <table className="taula">
            <thead>
              <tr>
                <th>{t('documents.versio')}</th>
                <th>{t('documents.data')}</th>
                <th>{t('documents.pujatPer')}</th>
                <th>{t('documents.notaCanvi')}</th>
                <th>{t('documents.mida')}</th>
                <th>{t('documents.estat')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {llista.map((v) => {
                const esVigent = v.id === vigent?.id;
                return (
                  <tr key={v.id} data-vigent={esVigent}>
                    <td className="mono">{t('documents.vN', { n: v.versio })}</td>
                    <td>{f.data(v.pujatAt)}</td>
                    <td>{v.pujatPer ?? '—'}</td>
                    <td className={esVigent ? undefined : 'dim'}>{v.comentari ?? v.nomFitxer}</td>
                    <td className="dim">{midaLlegible(v.mida)}</td>
                    <td>
                      {v.estatEscaneig !== 'NET' ? (
                        <span className={`badge ${badgeEscaneig(v.estatEscaneig)}`}>
                          {t(`documents.escaneig.${v.estatEscaneig}`)}
                        </span>
                      ) : (
                        <span className={`badge ${esVigent ? 'badge--ok' : 'badge--gray'}`}>
                          {esVigent ? t('documents.vigent') : t('documents.substituida')}
                        </span>
                      )}
                    </td>
                    <td>
                      {v.estatEscaneig === 'NET' && d.descarregable ? (
                        <button
                          type="button"
                          className="btn btn--ghost btn--xs"
                          aria-label={t('documents.descarregaVersio', { n: v.versio })}
                          title={t('documents.descarregaVersio', { n: v.versio })}
                          onClick={() => descarrega(v)}
                        >
                          <Icona nom="arrow-down" />
                        </button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PestanyaAcces({ d }: { d: DocumentAdminDto }) {
  const { t } = useTranslation('administracio');
  const ambit = useNomAmbit(d.serveiId);
  return (
    <div className="panel">
      <CapPanell titol={t('documents.ambitAcces')} icona="lock" colorIcona="c-blue" />
      <dl className="panel__body sis-dl">
        <div>
          <dt>{t('sistema.documents.quiConsulta')}</dt>
          <dd>
            {t(`sistema.documents.ambitFrase.${d.ambit}`, { nom: ambit.nom(d.ambit) })}
            <div className="t-caption dim">{t(`sistema.documents.ambitAjuda.${d.ambit}`)}</div>
          </dd>
        </div>
        <div>
          <dt>{t('documents.descarregable')}</dt>
          <dd>
            {d.descarregable ? t('documents.si') : t('documents.no')}
            <div className="t-caption dim">{t('sistema.documents.ajudaDescarrega')}</div>
          </dd>
        </div>
        <div>
          <dt>{t('documents.estat')}</dt>
          <dd>
            <span className={`badge ${badgeEstatDocument(d.estat)}`}>
              {t(`documents.estats.${d.estat}`)}
            </span>
            {d.estat !== 'PUBLICAT' ? (
              <div className="t-caption dim">{t('documents.notaPeu')}</div>
            ) : null}
          </dd>
        </div>
      </dl>
    </div>
  );
}

function PestanyaActivitat({ id }: { id: number }) {
  const { t } = useTranslation('administracio');
  const f = useFormat();
  const [mida, setMida] = useState(10);
  const auditoria = useAuditoria({ entitat: 'document', entitatId: id, page: 0, size: mida });
  const items = auditoria.data?.items ?? [];
  const total = auditoria.data?.total ?? 0;
  return (
    <div className="panel">
      <CapPanell titol={t('documents.auditoria')} icona="clock-arrow" />
      {items.length === 0 ? (
        <Buit icona="clipboard">
          {auditoria.isLoading ? t('comu.carregant') : t('documents.senseAuditoria')}
        </Buit>
      ) : (
        <>
          <div className="sis-taula-scroll">
            <table className="taula">
              <thead>
                <tr>
                  <th>{t('configuracio.quan')}</th>
                  <th>{t('sistema.documents.que')}</th>
                  <th>{t('configuracio.qui')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((a) => (
                  <tr key={a.id}>
                    <td className="nowrap dim">{f.dataHora(a.at)}</td>
                    <td>
                      {textAccioAuditoria(a.accio, t)}
                      {a.resultat !== 'OK' ? (
                        <span className="badge badge--critic" style={{ marginLeft: 6 }}>
                          {t('configuracio.resultatKo')}
                        </span>
                      ) : null}
                    </td>
                    <td>{a.upn ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {total > items.length ? (
            <div className="panel__foot">
              <div className="spacer" />
              <button type="button" className="btn btn--xs" onClick={() => setMida(mida + 20)}>
                {t('sistema.documents.mesActivitat')}
              </button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

export function W33DetallDocument() {
  const { t } = useTranslation('administracio');
  const missatgeError = useMissatgeError();
  const { pot } = useMe();
  const id = Number(useParams().id);
  const doc = useDocument(id);
  const versions = useVersionsDocument(id);
  const retira = useRetiraDocument();
  const restaura = useRestauraDocument();
  const ambit = useNomAmbit(doc.data?.serveiId ?? -1);
  const [pestanya, setPestanya] = useState<Pestanya>('dades');
  const [substitueix, setSubstitueix] = useState(false);
  const [retirant, setRetirant] = useState(false);
  const [motiu, setMotiu] = useState('');
  const [errorDescarrega, setErrorDescarrega] = useState<unknown>(null);

  if (doc.isError) return <ErrorCarrega onReintenta={() => void doc.refetch()} />;
  if (!doc.data) return <Carregant />;
  const d = doc.data;
  const vigent = d.versioVigent;
  const descarrega = (v?: DocumentVersioDto | null) => {
    setErrorDescarrega(null);
    apiDescarrega(
      `/documents/${d.id}/descarrega${v ? `?versioId=${v.id}` : ''}`,
      v?.nomFitxer ?? d.titol,
    ).catch(setErrorDescarrega);
  };
  const ext = (vigent ?? d.darreraVersio)?.nomFitxer.split('.').pop()?.toUpperCase();
  const retirat = d.estat === 'RETIRAT';
  const editable = pot('cai.documents', 'MODIFICAR') && !retirat;
  // La restauració pot no estar disponible encara al servidor: s'amaga si respon 404/405.
  const restauraNoDisponible =
    restaura.error instanceof ApiError && [404, 405].includes(restaura.error.status);
  const nVersions = versions.data?.length;

  const pestanyes: { clau: Pestanya; n?: number }[] = [
    { clau: 'dades' },
    { clau: 'versions', n: nVersions },
    { clau: 'acces' },
    { clau: 'activitat' },
  ];

  return (
    <section className="pantalla" data-screen="W-33">
      <Toolbar titol={t('titols.detallDocument')} enrere="/admin/documentacio">
        {d.descarregable && d.estat === 'PUBLICAT' ? (
          <button
            type="button"
            className="btn btn--outline btn--s"
            onClick={() => descarrega(vigent)}
          >
            <Icona nom="arrow-down" />
            {t('documents.descarrega')}
          </button>
        ) : null}
        {pot('cai.documents', 'ELIMINAR') && !retirat ? (
          <button
            type="button"
            className="btn btn--danger btn--s"
            onClick={() => setRetirant(true)}
          >
            <Icona nom="archive" />
            {t('documents.retira')}
          </button>
        ) : null}
        {pot('cai.documents', 'MODIFICAR') && retirat && !restauraNoDisponible ? (
          <button
            type="button"
            className="btn btn--outline btn--s"
            disabled={restaura.isPending}
            onClick={() => restaura.mutate(d.id)}
          >
            <Icona nom="arrow-clockwise" />
            {t('sistema.documents.restaura')}
          </button>
        ) : null}
        {editable ? (
          <button
            type="button"
            className="btn btn--dark btn--s"
            onClick={() => setSubstitueix(true)}
          >
            <Icona nom="arrow-up" />
            {t('documents.novaVersio')}
          </button>
        ) : null}
      </Toolbar>
      <div className="fitxa-cap">
        <div className="fitxa-cap__titols">
          <span className="fitxa-cap__ruta">{ambit.ruta || d.servei}</span>
          <h2>{d.titol}</h2>
          <div className="fitxa-cap__badges">
            <span className={`badge ${badgeEstatDocument(d.estat)}`}>
              {t(`documents.estats.${d.estat}`)}
            </span>
            <span className="badge badge--gray mono">{d.codi ?? `#${d.id}`}</span>
            {ext ? (
              <span className="badge badge--gray">
                {ext}
                {vigent ? ` · ${midaLlegible(vigent.mida)}` : ''}
              </span>
            ) : null}
            {vigent ? (
              <span className="badge badge--gray">{t('documents.vN', { n: vigent.versio })}</span>
            ) : null}
          </div>
        </div>
      </div>
      <div className="fitxa-pestanyes" role="tablist" aria-label={d.titol}>
        {pestanyes.map((p) => (
          <button
            key={p.clau}
            type="button"
            role="tab"
            aria-selected={pestanya === p.clau}
            onClick={() => setPestanya(p.clau)}
          >
            {t(`sistema.documents.pestanyes.${p.clau}`)}
            {p.n != null ? <span className="fitxa-pestanyes__n">{p.n}</span> : null}
          </button>
        ))}
      </div>
      <div className="fitxa-cos">
        {d.motiuEstat && d.estat !== 'PUBLICAT' ? (
          <div className="avis-banda" role="status">
            <Icona nom={retirat ? 'archive' : 'lock'} />
            <span>
              <strong>{t('documents.motiu')}:</strong> {d.motiuEstat}
            </span>
          </div>
        ) : null}
        {errorDescarrega ? (
          <p className="avis-banda">
            <Icona nom="exclamationmark-triangle" />
            {missatgeError(errorDescarrega)}
          </p>
        ) : null}
        {restaura.isError && !restauraNoDisponible ? (
          <p className="error-text">{missatgeError(restaura.error)}</p>
        ) : null}
        {restauraNoDisponible ? (
          <p className="sis-nota-gris">{t('sistema.documents.restauraNoDisponible')}</p>
        ) : null}
        {pestanya === 'dades' ? (
          <PestanyaDades key={`${d.id}-${d.updatedAt ?? ''}`} d={d} editable={editable} />
        ) : pestanya === 'versions' ? (
          <PestanyaVersions d={d} descarrega={descarrega} />
        ) : pestanya === 'acces' ? (
          <PestanyaAcces d={d} />
        ) : (
          <PestanyaActivitat id={d.id} />
        )}
      </div>
      {substitueix ? <W34PujaDocument document={d} onTanca={() => setSubstitueix(false)} /> : null}
      {retirant ? (
        <Confirmacio
          titol={t('sistema.documents.retiraTitol', { titol: d.titol })}
          textAccio={t('documents.confirmaRetirada')}
          perill
          ocupat={!motiu.trim() || retira.isPending}
          onTanca={() => setRetirant(false)}
          onConfirma={() =>
            retira.mutate(
              { id: d.id, motiu: motiu.trim() },
              { onSuccess: () => setRetirant(false) },
            )
          }
        >
          <p>{t('sistema.documents.retiraText')}</p>
          <Camp etiqueta={t('documents.motiu')}>
            <textarea
              className="input"
              value={motiu}
              autoFocus
              onChange={(e) => setMotiu(e.target.value)}
            />
          </Camp>
          {retira.isError ? <p className="error-text">{missatgeError(retira.error)}</p> : null}
        </Confirmacio>
      ) : null}
    </section>
  );
}
